// ===========================================
// Compose Message Component
// ===========================================
// Text input with send button for composing messages

import { useState, KeyboardEvent } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';

// ===========================================
// TYPES
// ===========================================

interface ComposeMessageProps {
  onSend: (content: string) => void;
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

// ===========================================
// CONSTANTS
// ===========================================

const MAX_LENGTH = 5000;

// ===========================================
// COMPONENT
// ===========================================

export default function ComposeMessage({
  onSend,
  loading = false,
  disabled = false,
  placeholder = 'Type a message...',
}: ComposeMessageProps) {
  const [content, setContent] = useState('');

  const canSend = content.trim().length > 0 && content.length <= MAX_LENGTH && !loading && !disabled;

  const handleSend = () => {
    if (!canSend) return;

    onSend(content.trim());
    setContent('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Send on Enter (without Shift for newline)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const remainingChars = MAX_LENGTH - content.length;
  const showCharCount = content.length > MAX_LENGTH - 500;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        p: 2,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, position: 'relative' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={loading || disabled}
          error={content.length > MAX_LENGTH}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              bgcolor: 'grey.50',
            },
          }}
        />
        {showCharCount && (
          <Typography
            variant="caption"
            color={remainingChars < 0 ? 'error' : 'text.secondary'}
            sx={{
              position: 'absolute',
              right: 8,
              bottom: -18,
            }}
          >
            {remainingChars} characters remaining
          </Typography>
        )}
      </Box>

      <IconButton
        color="primary"
        onClick={handleSend}
        disabled={!canSend}
        sx={{
          bgcolor: canSend ? 'primary.main' : 'grey.300',
          color: canSend ? 'primary.contrastText' : 'text.disabled',
          '&:hover': {
            bgcolor: canSend ? 'primary.dark' : 'grey.300',
          },
          width: 44,
          height: 44,
        }}
      >
        {loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <SendIcon />
        )}
      </IconButton>
    </Box>
  );
}
